import type { FormConfigField } from '@/common/store/BookStore';
import type {
    AccommodationExtra,
    AccommodationLocation,
    ArrangementCalendarInfo,
    AvailableAccommodation,
    ExistingBooking,
    MadeBooking,
    MetaData,
    Payment,
    PriceCalculation,
    Transaction,
} from '@/widgets/Wizard/models/wizard';
import type { Ref } from 'vue';

export abstract class TommyGateway {
    public abstract getMetaData(salesChannel?: number): Promise<MetaData | null>;

    public abstract getCountries(locale: string): Promise<{ code: string; name: string }[] | null>;

    public abstract checkAvailability(
        arrivalDate: string,
        departureDate: string,
        amenities: { id: string }[],
        ageCategories?: { [id: string]: number },
        salesChannel?: number,
    ): Promise<AvailableAccommodation[] | null>;

    public abstract getAccommodationLocations(
        arrivalDate: Date,
        departureDate: Date,
        accommodationId: number,
        amenities: { id: string }[],
        ageCategories?: { [id: string]: number },
        salesChannel?: number,
    ): Promise<AccommodationLocation[] | null>;

    public abstract getAccommodationBookingExtras(
        arrivalDate: Date,
        departureDate: Date,
        accommodationId: number,
        amenities: { id: string }[],
        ageCategories?: { [id: string]: number },
        locationId?: number,
        salesChannel?: number,
    ): Promise<AccommodationExtra[] | null>;

    public abstract getPriceCalculation(
        locale: string,
        arrivalDate: Date,
        departureDate: Date,
        accommodationId: number,
        amenities: { id: string }[],
        ageCategories?: { [id: string]: number },
        locationId?: number,
        extras?: { [id: string]: number },
        salesChannel?: number,
        arrangements?: { id: number | string }[],
    ): Promise<PriceCalculation | null>;

    public abstract getArrangementCalendarInfo(
        arrangementIds: string[],
    ): Promise<ArrangementCalendarInfo[] | null>;

    public abstract handleDetailsChange(field: FormConfigField, modelData: Ref<any>): Promise<any>;

    public abstract book(
        arrivalDate: Date,
        departureDate: Date,
        accommodationId: number,
        extras: { [id: string]: number },
        ageCategories: { [id: string]: number },
        customerFields: { [field: string]: any },
        locale: string,
        locationId?: number,
        salesChannel?: number,
        arrangements?: { id: number | string }[],
    ): Promise<MadeBooking | null>;

    public abstract getBooking(bookingNumber: string): Promise<ExistingBooking | null>;

    public abstract bookingPaid(bookingId: number): Promise<boolean | null>;

    public abstract getPaymentForBooking(bookingId: number): Promise<Payment | null>;

    public abstract startBookingPayment(bookingId: number): Promise<Transaction | null>;

    public abstract markBookingAsArrived(bookingId: number): Promise<boolean | null>;
}
